import React from "react";

import Centile from "pages/customer/tools/location/filters/targets/Centile";
import CentileMetric from "pages/customer/tools/location/filters/targets/CentileMetric";

import {
    selectCandidateComparatorStores,
    selectCandidateTarget,
    selectAggregatedDirectCompetitors,
    selectRetailCentresCentiles,
    selectStoreCategoryCentilesMetrics,
    setCandidateTarget
} from "modules/customer/tools/location/filters/filtersSlice";
import { useAppDispatch, useAppSelector } from "store";
import numberFormatter from "utils/numberFormatter";

const Children: React.FC = () => {
    const dispatch = useAppDispatch();
    const candidateComparatorStores = useAppSelector(selectCandidateComparatorStores);
    const retailCentresCentiles = useAppSelector(selectRetailCentresCentiles);
    const comparatorStoresCentiles = candidateComparatorStores.map(store => {
        const retailCentreCentile = retailCentresCentiles.find(rcc => rcc.retailCentreId === store.retailCentre.id)?.children ?? 0;
        return {
            storeName: store.name,
            centileValue: retailCentreCentile
        };
    });
    const aggregatedDirectCompetitors = useAppSelector(selectAggregatedDirectCompetitors);
    const aggregatedDirectCompetitorCentiles = aggregatedDirectCompetitors.map(item => ({
        storeName: item.fascia,
        centileValue: item.childrenCentile
    }));
    const candidateTarget = useAppSelector(selectCandidateTarget);
    const storeCategoryCentilesMetrics = useAppSelector(selectStoreCategoryCentilesMetrics);
    const centileMetric = storeCategoryCentilesMetrics.find(cm => cm.centile === candidateTarget?.children);
    const name = "Children";
    const description = `The children score looks at the catchment area population and evaluates what percentage of
        residents are children and how young these children are.`;
    const childrenCentileMetrics = [{
        name: "Aged 0-5",
        tooltip: "The percentage of the catchment area's population aged 0 to 5.",
        value: numberFormatter.toPercentage(centileMetric?.children0To5Percentage ?? 0, false, 0)
    }, {
        name: "Aged 6-10",
        tooltip: "The percentage of the catchment area's population aged 6 to 10.",
        value: numberFormatter.toPercentage(centileMetric?.children6To10Percentage ?? 0, false, 0)
    }, {
        name: "Aged 11-16",
        tooltip: "The percentage of the catchment area's population aged 11 to 16.",
        value: numberFormatter.toPercentage(centileMetric?.children11To16PlusPercentage ?? 0, false, 0)
    }];

    const handleSliderChange = (value: number) => {
        if (candidateTarget) {
            const newCandidateTarget = {
                ...candidateTarget,
                children: value
            };
            dispatch(setCandidateTarget(newCandidateTarget));
        }
    };

    if (!candidateTarget) {
        return null;
    }

    return (
        <Centile
            name={name}
            description={description}
            disabled={!candidateTarget.useDemographics}
            sliderValue={candidateTarget.children}
            onSliderChange={handleSliderChange}
            comparatorStoresCentiles={comparatorStoresCentiles}
            aggregatedDirectCompetitorCentiles={aggregatedDirectCompetitorCentiles}
            chartLowLabel="Adult-only households"
            chartHighLabel="Households with a high proportion of young children"
        >
            {childrenCentileMetrics.map((metric, index) =>
                <CentileMetric
                    key={index}
                    name={metric.name}
                    tooltip={metric.tooltip}
                    value={metric.value}
                    disabled={!candidateTarget.useDemographics}
                />
            )}
        </Centile>
    );
};

export default Children;
